import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertCircle,
  Calendar,
  User,
  FileText,
  MessageSquare,
  X,
  ArrowRight,
  Download,
  ZoomIn,
  MapPin,
  CheckCircle2,
  Camera,
  ChevronLeft,
  ChevronRight,
  Building2,
  AlertTriangle,
  Info,
  Edit,
} from 'lucide-react';
import type { AuditException } from '@/types/asset-audit';
import { format } from 'date-fns';
import { AuditorNotesSection } from './AuditorNotesSection';
import { RelatedInformationSection } from './RelatedInformationSection';

interface ExceptionDetailPanelProps {
  exception: AuditException | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateStatus: (exceptionId: string, status: string) => void;
  onAddComment: (exceptionId: string, comment: string) => void;
}

const getSeverityColor = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
    case 'high':
      return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800';
    case 'low':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
  }
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'open':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'in progress':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'resolved':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'closed':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
};

const getSeverityIcon = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'critical':
      return <AlertTriangle className="h-4 w-4" />;
    case 'high':
      return <AlertCircle className="h-4 w-4" />;
    case 'medium':
      return <Info className="h-4 w-4" />;
    case 'low':
      return <Info className="h-4 w-4" />;
    default:
      return <Info className="h-4 w-4" />;
  }
};

// Convert camelCase/PascalCase to readable format
const formatExceptionType = (type: string): string => {
  return type
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/^./, (str) => str.toUpperCase());
};

export const ExceptionDetailPanel: React.FC<ExceptionDetailPanelProps> = ({
  exception,
  isOpen,
  onClose,
  onUpdateStatus,
  onAddComment,
}) => {
  const [newComment, setNewComment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [imageError, setImageError] = useState<Record<string, boolean>>({});

  // Handle keyboard events for photo viewer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedPhotoIndex !== null) {
        if (e.key === 'Escape') {
          setSelectedPhotoIndex(null);
        } else if (e.key === 'ArrowLeft') {
          handlePreviousPhoto();
        } else if (e.key === 'ArrowRight') {
          handleNextPhoto();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhotoIndex]);

  if (!exception) return null;

  const handlePreviousPhoto = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const handleNextPhoto = () => {
    if (exception.images && selectedPhotoIndex !== null && selectedPhotoIndex < exception.images.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    }
  };

  const downloadImage = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleStatusUpdate = () => {
    if (selectedStatus && selectedStatus !== exception.status) {
      onUpdateStatus(exception.id, selectedStatus);
      setSelectedStatus('');
      setIsStatusDialogOpen(false);
    }
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      onAddComment(exception.id, newComment);
      setNewComment('');
    }
  };

  // Limit images to 5 (business rule)
  const displayImages = exception.images?.slice(0, 5) || [];

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-3xl p-0 flex flex-col gap-0 bg-white dark:bg-black">
          {/* Fixed Header */}
          <div className="px-6 py-4 border-b bg-white dark:bg-black flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-2xl font-semibold truncate">
                    {formatExceptionType(exception.exceptionType)}
                  </h2>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${getSeverityColor(exception.severity)} flex items-center gap-1`}>
                    {getSeverityIcon(exception.severity)}
                    {exception.severity}
                  </Badge>
                  <Badge className={getStatusColor(exception.status)}>
                    {exception.status}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(exception.reportedDate), 'MMM dd, yyyy')}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsStatusDialogOpen(true)}
                className="flex-shrink-0"
              >
                <Edit className="h-4 w-4 mr-2" />
                Update Status
              </Button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-hidden bg-white dark:bg-black">
            <Tabs defaultValue="overview" className="h-full flex flex-col">
              <div className="flex-shrink-0 border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
                <TabsList className="h-auto p-0 bg-transparent border-0 gap-1">
                  <TabsTrigger 
                    value="overview"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Overview
                  </TabsTrigger>
                  <TabsTrigger 
                    value="photos"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Photos {displayImages.length > 0 && `(${displayImages.length})`}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="comments"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Comments
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Overview Tab */}
              <TabsContent value="overview" className="flex-1 overflow-y-auto px-6 pb-6 mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                <div className="space-y-6 py-6">
                  {/* Description */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Description</Label>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed pl-7">
                      {exception.description}
                    </p>
                  </div>

                  <Separator />

                  {/* Asset Information Card */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Asset Information</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Asset Name</p>
                          <p className="text-sm font-medium">{exception.assetName}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Asset ID</p>
                          <p className="text-sm font-medium font-mono">{exception.assetId}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Discrepancy Comparison */}
                  {(exception.expectedValue || exception.actualValue) && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <ArrowRight className="h-5 w-5 text-muted-foreground" />
                          <Label className="text-base font-semibold">Discrepancy Details</Label>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-xs text-muted-foreground">Expected Value</Label>
                              <div className="bg-background rounded-md p-3 border border-dashed">
                                <p className="text-sm line-through text-muted-foreground">
                                  {exception.expectedValue || 'N/A'}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs text-muted-foreground">Actual Value</Label>
                              <div className="bg-orange-50 dark:bg-orange-950/20 rounded-md p-3 border border-orange-200 dark:border-orange-900">
                                <p className="text-sm font-semibold text-orange-700 dark:text-orange-400">
                                  {exception.actualValue || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>
                          {exception.exceptionType === 'LocationMismatch' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-4 w-full"
                              onClick={() => {
                                // TODO: Implement map view
                                alert('Map view coming soon!');
                              }}
                            >
                              <MapPin className="h-4 w-4 mr-2" />
                              View on Map
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <Separator />

                  {/* Assignment & Timeline */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Assignment & Timeline</Label>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Assigned To</span>
                        </div>
                        <span className="text-sm font-medium">
                          {exception.assignedToName || 'Unassigned'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Date Identified</span>
                        </div>
                        <span className="text-sm font-medium">
                          {format(new Date(exception.reportedDate), 'MMM dd, yyyy')}
                        </span>
                      </div>
                      {exception.resolvedDate && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <span className="text-xs text-muted-foreground">Date Resolved</span>
                          </div>
                          <span className="text-sm font-medium">
                            {format(new Date(exception.resolvedDate), 'MMM dd, yyyy')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Related Information */}
                  <RelatedInformationSection exception={exception} assetId={exception.assetId} />

                  <Separator />

                  {/* Auditor Notes */}
                  <AuditorNotesSection exception={exception} />
                </div>
              </TabsContent>

              {/* Photos Tab */}
              <TabsContent value="photos" className="flex-1 overflow-y-auto px-6 pb-6 mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                <div className="py-6">
                  {displayImages.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Camera className="h-5 w-5 text-muted-foreground" />
                          <Label className="text-base font-semibold">
                            Exception Photos
                          </Label>
                          <Badge variant="secondary">{displayImages.length}/5</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {displayImages.map((image, index) => (
                          <button
                            key={`image-${image.url}`}
                            type="button"
                            className="relative aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-80 transition-opacity group text-left"
                            onClick={() => setSelectedPhotoIndex(index)}
                          >
                            {!imageError[image.url] ? (
                              <>
                                <img
                                  src={image.url}
                                  alt={`Exception ${index + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={() => setImageError({ ...imageError, [image.url]: true })}
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                  <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Camera className="h-8 w-8 text-muted-foreground" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                              <p className="text-white text-xs">Photo {index + 1}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                      {exception.images && exception.images.length > 5 && (
                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                          <p className="text-sm text-muted-foreground">
                            Showing first 5 of {exception.images.length} photos
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Camera className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground">No photos attached</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Comments Tab */}
              <TabsContent value="comments" className="flex-1 overflow-y-auto px-6 pb-6 mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                <div className="space-y-6 py-6">
                  {/* Comments Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-muted-foreground" />
                      <Label className="text-base font-semibold">Comments & Updates</Label>
                    </div>

                    {/* Existing Comments */}
                    {exception.comments && exception.comments.length > 0 ? (
                      <div className="space-y-3">
                        {exception.comments.map((comment) => (
                          <div key={comment.id} className="bg-muted/50 rounded-lg p-4 space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-xs font-medium text-primary">
                                    {comment.userName.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm font-medium">{comment.userName}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(comment.createdDate), 'MMM dd, yyyy HH:mm')}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed pl-10">
                              {comment.comment}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-muted/30 rounded-lg">
                        <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No comments yet</p>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Add Comment */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Add Comment</Label>
                    <div className="space-y-2">
                      <Textarea
                        placeholder="Type your comment..."
                        value={newComment}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewComment(e.target.value)}
                        rows={4}
                        className="resize-none"
                      />
                      <div className="flex justify-end">
                        <Button onClick={handleAddComment} disabled={!newComment.trim()}>
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Add Comment
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>

      {/* Update Status Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Exception Status</DialogTitle>
            <DialogDescription>
              Change the status of this exception. This will be recorded in the activity log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Status</Label>
              <div className="flex items-center gap-2">
                <Badge className={getStatusColor(exception.status)}>
                  {exception.status}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate} disabled={!selectedStatus || selectedStatus === exception.status}>
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full-screen Photo Viewer */}
      {selectedPhotoIndex !== null && displayImages[selectedPhotoIndex] && createPortal(
        <dialog
          open
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center m-0 p-0 border-none max-w-none max-h-none w-screen h-screen"
        >
          <button
            type="button"
            className="absolute inset-0 w-full h-full bg-transparent cursor-default"
            onClick={() => setSelectedPhotoIndex(null)}
            onKeyDown={(e) => { if (e.key === 'Escape') { setSelectedPhotoIndex(null); } }}
            aria-label="Close photo viewer"
          />
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-[10000] text-white hover:bg-white/20 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              console.log('Close button clicked');
              setSelectedPhotoIndex(null);
            }}
          >
            <X className="h-6 w-6" />
          </Button>

          {selectedPhotoIndex > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-[10000] text-white hover:bg-white/20 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                handlePreviousPhoto();
              }}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
          )}

          <div className="max-w-5xl max-h-[90vh] flex flex-col items-center relative z-10">
            <img
              src={displayImages[selectedPhotoIndex].url}
              alt={`Exception ${selectedPhotoIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            
            <div className="mt-4 flex items-center gap-4">
              <p className="text-white/80 text-sm">
                Photo {selectedPhotoIndex + 1} of {displayImages.length}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  downloadImage(
                    displayImages[selectedPhotoIndex].url,
                    `exception-${exception.id}-photo-${selectedPhotoIndex + 1}.jpg`
                  )
                }
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>

          {selectedPhotoIndex < displayImages.length - 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-[10000] text-white hover:bg-white/20 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                handleNextPhoto();
              }}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          )}
        </dialog>,
        document.body
      )}
    </>
  );
};
